import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { serviceService, contentService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const Services = () => {
  const [services, setServices] = useState([]);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getText, isEnglish } = useLanguage();

  // Default values for other services
  const defaultOtherServices = [
    {
      title: '保險安排',
      description: '協助僱主購買合適的僱員補償保險及醫療保險。',
      items: ['僱員補償保險', '醫療保險', '意外保障']
    },
    {
      title: '醫療檢查',
      description: '安排女傭進行所需的醫療檢查，確保健康狀況良好。',
      items: ['全身體格檢查', 'X光檢查', '懷孕檢測']
    },
    {
      title: '簽證申請',
      description: '全程跟進簽證申請，確保順利及時。',
      items: ['入境處申請', '領事館公證', '簽證續期']
    }
  ];

  const defaultOtherServicesEn = [
    {
      title: 'Insurance Arrangement',
      description: 'Help employers purchase appropriate employee compensation insurance and medical insurance.',
      items: ['Employee Compensation Insurance', 'Medical Insurance', 'Accident Coverage']
    },
    {
      title: 'Medical Examination',
      description: 'Arrange necessary medical examinations for helpers to ensure good health.',
      items: ['Full Body Checkup', 'X-ray Examination', 'Pregnancy Test']
    },
    {
      title: 'Visa Application',
      description: 'Full follow-up on visa applications to ensure smooth and timely processing.',
      items: ['Immigration Application', 'Consulate Authentication', 'Visa Renewal']
    }
  ];

  const defaultImportantInfo = [
    '服務保證：我們提供兩年合約保證，如女傭不合適可免費更換。',
    '付款方式：接受現金、支票及轉帳付款。',
    '處理時間：海外女傭約8-12星期，現成女傭約4-6星期。',
    '免費服務：提供免費咨詢、配對及跟進服務。',
    '特別優惠：定期推出優惠計劃，請留意我們的最新資訊。'
  ];

  const defaultImportantInfoEn = [
    'Service Guarantee: We provide a two-year contract guarantee with free replacement if the helper is unsuitable.',
    'Payment Methods: Cash, cheque, and bank transfer accepted.',
    'Processing Time: Overseas helpers take about 8-12 weeks, local helpers take about 4-6 weeks.',
    'Free Services: Free consultation, matching, and follow-up services.',
    'Special Offers: Regular promotional plans available, please check our latest news.'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, contentRes] = await Promise.all([
        serviceService.getAll(),
        contentService.get()
      ]);
      setServices(servicesRes.data);
      setContent(contentRes.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get other services with correct language
  const getOtherServices = () => {
    if (!content?.otherServices || content.otherServices.length === 0) {
      return isEnglish ? defaultOtherServicesEn : defaultOtherServices;
    }
    
    if (isEnglish) {
      return content.otherServices.map((svc, idx) => ({
        title: svc.titleEn || defaultOtherServicesEn[idx]?.title || svc.title,
        description: svc.descriptionEn || defaultOtherServicesEn[idx]?.description || svc.description,
        items: svc.itemsEn?.length > 0 ? svc.itemsEn : (defaultOtherServicesEn[idx]?.items || svc.items)
      }));
    }
    return content.otherServices;
  };
  
  // Get important info with correct language
  const getImportantInfo = () => {
    if (!content?.importantInfo || content.importantInfo.length === 0) {
      return isEnglish ? defaultImportantInfoEn : defaultImportantInfo;
    }
    
    if (isEnglish) {
      if (content.importantInfoEn && content.importantInfoEn.length > 0) {
        return content.importantInfoEn.map((en, idx) => en || defaultImportantInfoEn[idx] || content.importantInfo[idx]);
      }
      return defaultImportantInfoEn;
    }
    return content.importantInfo;
  };

  const otherServices = getOtherServices();
  const importantInfo = getImportantInfo();
  const showOtherServices = content?.showOtherServices !== false;
  const showImportantInfo = content?.showImportantInfo !== false;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-lime-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">
              <span style={{ color: '#00b6a5' }}>{getText('服務收費', 'Service Pricing')}</span>
            </h1>
            <p className="text-xl text-gray-600">{getText('透明收費，無隱藏費用', 'Transparent pricing, no hidden fees')}</p>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {services.map((service, index) => (
              <div key={service.id} className="">
                <Card className="overflow-hidden border-2 hover:shadow-2xl transition-all duration-300">
                  <CardHeader className="text-center py-8" style={{ background: 'linear-gradient(135deg, #00b6a5 0%, #00d4bd 100%)' }}>
                    <CardTitle className="text-3xl text-white">
                      {isEnglish ? (service.titleEn || service.title) : service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Pricing */}
                      <div className="space-y-6">
                        <div className="bg-gradient-to-br from-teal-50 to-white p-6 rounded-xl">
                          <h3 className="text-xl font-bold mb-4" style={{ color: '#00b6a5' }}>
                            {getText('菲律賓女傭', 'Filipino Helper')}
                          </h3>
                          <p className="text-4xl font-bold mb-2" style={{ color: '#00b6a5' }}>{service.filipinoPrice}</p>
                          <p className="text-sm text-gray-600">{getText('包括全套服務及保證', 'Including full service and guarantee')}</p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-lime-50 to-white p-6 rounded-xl">
                          <h3 className="text-xl font-bold mb-4" style={{ color: '#00b6a5' }}>
                            {getText('印尼女傭', 'Indonesian Helper')}
                          </h3>
                          <p className="text-4xl font-bold mb-2" style={{ color: '#00b6a5' }}>{service.indonesianPrice}</p>
                          <p className="text-sm text-gray-600">{getText('包括全套服務及保證', 'Including full service and guarantee')}</p>
                        </div>
                      </div>

                      {/* Inclusions */}
                      <div>
                        <h3 className="text-2xl font-bold mb-6" style={{ color: '#00b6a5' }}>
                          {getText('服務包括：', 'Services Included:')}
                        </h3>
                        <ul className="space-y-3">
                          {(isEnglish && service.includedEn?.length > 0 ? service.includedEn : service.included).map((item, idx) => (
                            <li key={idx} className="flex items-start">
                              <CheckCircle className="h-6 w-6 mr-3 mt-0.5 flex-shrink-0" style={{ color: '#00b6a5' }} />
                              <span className="text-lg text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-8 pt-6 border-t flex justify-center">
                      <Link to="/contact">
                        <Button size="lg" style={{ backgroundColor: '#00b6a5' }}>
                          {getText('立即查詢', 'Inquire Now')}
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services - Dynamic */}
      {showOtherServices && (
      <section className="py-16" style={{ backgroundColor: '#f8fffe' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              <span style={{ color: '#00b6a5' }}>{getText('其他服務', 'Other Services')}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {otherServices.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle style={{ color: '#00b6a5' }}>{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    {service.description}
                  </p>
                  <ul className="space-y-2 text-sm">
                    {service.items.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-4 w-4 mr-2 mt-0.5" style={{ color: '#00b6a5' }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Important Notes - Dynamic */}
      {showImportantInfo && (
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2" style={{ borderColor: '#00b6a5' }}>
            <CardHeader style={{ backgroundColor: '#f8fffe' }}>
              <CardTitle className="text-2xl" style={{ color: '#00b6a5' }}>
                {getText('重要資訊', 'Important Information')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4 text-gray-700">
                {importantInfo.map((info, index) => {
                  // Split by colon to bold the title part
                  const colonIndex = info.indexOf('：') > 0 ? info.indexOf('：') : info.indexOf(':');
                  if (colonIndex > 0) {
                    const title = info.substring(0, colonIndex);
                    const content = info.substring(colonIndex + 1);
                    return (
                      <p key={index}><strong>• {title}:</strong>{content}</p>
                    );
                  }
                  return <p key={index}><strong>•</strong> {info}</p>;
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      )}

      {/* CTA Section */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #00b6a5 0%, #00d4bd 100%)' }}>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            {getText('有疑問？立即聯絡我們', 'Have Questions? Contact Us Now')}
          </h2>
          <p className="text-xl text-white mb-8">
            {getText('24小時客戶支援熱線', '24-Hour Customer Support Hotline')}: {content?.phone || '+852 6187 1388'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white px-8 py-6 text-lg" style={{ color: '#00b6a5' }}>
                {getText('立即查詢', 'Inquire Now')}
              </Button>
            </Link>
            <a href={`https://api.whatsapp.com/send?phone=${(content?.whatsapp || '85261871388').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="px-8 py-6 text-lg" style={{ backgroundColor: '#00b6a5', color: '#000' }}>
                {getText('WhatsApp 聯絡', 'WhatsApp Contact')}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
